import getLinkToDetail from 'helpers/getLinkToDetail'
import getTitle from 'helpers/getTitle'
import {formatDate, renderFromToDate} from 'helpers/datetimeHelpers'
import dayjs from 'dayjs'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext} from 'react'
import {BsCalendar3, BsClock, BsInfoCircle, BsMegaphoneFill, BsPeopleFill} from 'react-icons/bs'
import {FaUserTie} from 'react-icons/fa6'
import {IoChevronBack} from 'react-icons/io5'
import {MdSchool} from 'react-icons/md'
import {Link} from 'react-router-dom'
import _ from 'lodash'
import {getResponseItem, getResponseItems, NullAsDefault, renderIf} from 'views/Shared'
import CourseClassContext from '../../CourseClassContext'
import {bindQueryParam} from '../../../functions/routerHelper'
import {announcement_gets_Api, timetable_getMilestonesByCourseClass_Api} from 'apis'
import useAsync from 'modules/asyncCache/useAsync'
import {getId} from 'apis/model/base'
import {PiClockCountdownFill} from 'react-icons/pi'
import {HeaderWithIconLine} from '../../../Templates/items/ListHeader'
import {GoDotFill} from 'react-icons/go'
import {AnnouncementTypes} from '../../../Organization/consts/AnnouncementTypes'

const DateProgressBar = ({from_date, to_date, barHeight = 'h-2'}) => {
  const t = useTranslate()
  const now = dayjs()
  const start = dayjs(from_date)
  const end = dayjs(to_date)

  let percent, status
  if (now.isBefore(start)) {
    percent = 0
    status = 'upcoming'
  } else if (now.isAfter(end)) {
    percent = 100
    status = 'ended'
  } else {
    percent = Math.round((now.diff(start) / end.diff(start)) * 100)
    status = 'active'
  }

  const statusConfig = {
    upcoming: {
      label: t('not started yet'),
      badgeCls: 'text-color-400 bg-color-50 border border-color-100',
      barCls: 'bg-color-200',
    },
    active: {
      label: t('in progress'),
      badgeCls: 'text-primary bg-primary-50 border border-primary-100',
      barCls: 'bg-primary',
    },
    ended: {
      label: t('ended'),
      badgeCls: 'text-green-600 bg-green-50 border border-green-200',
      barCls: 'bg-green-500',
    },
  }
  const {label, badgeCls, barCls} = statusConfig[status]

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <BsClock
            size={12}
            className="text-color-400 flex-shrink-0"
          />
          <span className="text-xs text-color-500 italic">{`${t('time')} :`}</span>
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1 ${badgeCls}`}>
          {label}
          <span className="opacity-60">· {percent}%</span>
        </span>
      </div>
      <div className={`w-full ${barHeight} bg-color-50 rounded-full overflow-hidden`}>
        <div
          className={`h-full rounded-full ${barCls}`}
          style={{width: `${percent}%`}}
        />
      </div>
      <div className="flex items-center justify-between text-xs text-color-400">
        <span>{start.format('DD/MM/YYYY')}</span>
        <span>{end.format('DD/MM/YYYY')}</span>
      </div>
    </div>
  )
}

const CourseContextCard = ({course}) => {
  const t = useTranslate()
  if (!course) return null
  const {time_open, time_close, description: courseDesc} = course
  const hasBothDates = time_open && time_close

  return (
    <div className="background-50 rounded-xl border border-color-50 p-3 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <MdSchool
          size={13}
          className="text-color-400 flex-shrink-0"
        />
        <span className="text-xs text-color-500 italic">
          {`${t('belongs to the course')} :`}
        </span>
      </div>
      <Link
        to={getLinkToDetail(course)}
        className="flex items-center gap-2 font-bold text-base text-primary no-underline leading-tight group">
        <IoChevronBack
          size={14}
          className="text-primary-400 flex-shrink-0 opacity-70 group-hover:opacity-100"
        />
        {getTitle(course)}
      </Link>
      {renderIf(
        courseDesc,
        <p className="text-xs text-color-400 italic m-0 leading-snug ml-5">{courseDesc}</p>
      )}
      {hasBothDates
        ? (
          <div className="ml-5">
            <DateProgressBar
              from_date={time_open}
              to_date={time_close}
              barHeight="h-4"
            />
          </div>
        )
        : renderIf(
            _.some([time_open, time_close]),
            <div className="flex items-center gap-2 ml-5">
              <BsCalendar3
                size={11}
                className="text-color-400 flex-shrink-0"
              />
              <span className="text-xs text-color-400 italic">
                {renderFromToDate(time_open, time_close)}
              </span>
            </div>
          )}
    </div>
  )
}

const ClassMetaCard = ({item}) => {
  const t = useTranslate()
  const {
    from_date,
    to_date,
    description,
    about,
    number_of_students,
    coordinators = [],
  } = item ?? {}

  const hasBothDates = from_date && to_date

  return (
    <div className="background-50 rounded-xl border border-color-50 p-3 flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <BsInfoCircle
          size={13}
          className="text-color-400 flex-shrink-0"
        />
        <span className="text-xs font-semibold text-color-400 uppercase tracking-wide">
          {t('class information')}
        </span>
      </div>

      {renderIf(
        description,
        <p className="text-sm text-color-300 italic m-0 leading-snug">{description}</p>
      )}

      {renderIf(
        about,
        <p className="text-sm text-color-200 m-0 leading-relaxed border-l-2 border-primary-100 pl-3">
          {about}
        </p>
      )}

      {hasBothDates
        ? <DateProgressBar
            from_date={from_date}
            to_date={to_date}
          />
        : renderIf(
            _.some([from_date, to_date]),
            <div className="flex items-start gap-2">
              <BsClock
                size={13}
                className="text-color-400 flex-shrink-0 mt-0.5"
              />
              <div className="flex flex-col gap-1">
                <span className="text-xs text-color-500 italic">{`${t('time')} :`}</span>
                <span className="text-sm font-semibold text-color-100 leading-tight">
                  {renderFromToDate(from_date, to_date)}
                </span>
              </div>
            </div>
          )}

      <div className="flex items-center gap-2">
        <BsPeopleFill
          size={13}
          className="text-color-400 flex-shrink-0"
        />
        <span className="text-xs text-color-500 italic">{`${t('number of student')} :`}</span>
        <span className="text-sm font-semibold text-color-100">
          {`${number_of_students ?? 0} ${t('students')}`}
        </span>
      </div>

      {renderIf(
        coordinators?.length > 0,
        <div className="flex items-start gap-2">
          <FaUserTie
            size={13}
            className="text-color-400 flex-shrink-0 mt-0.5"
          />
          <div className="flex flex-wrap gap-x-1">
            <span className="text-xs text-color-500 italic">{`${t('coordinators')} : `}</span>
            {coordinators.map((e, i) => {
              const {teacher} = e ?? {}
              return (
                <span
                  key={i}
                  className="font-semibold text-sm text-color-000">
                  {`${teacher?.prefix_title || ''} ${teacher?.fullname}`}
                  {i < coordinators.length - 1 && ', '}
                </span>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

const ClassUpcomingTimetable = () => {
  const t = useTranslate()
  const {item} = useContext(CourseClassContext)
  const id = getId(item)

  const {response, isLoading} = useAsync({
    apiInfo: timetable_getMilestonesByCourseClass_Api,
    query: bindQueryParam({id}),
  })

  const cache = getResponseItem(response)
  const data = _.get(cache, 'data', [])

  const today = dayjs().startOf('day')
  const tomorrow = dayjs().add(1, 'day').endOf('day')

  const upcoming = _.chain(data || [])
    .flatMap(m =>
      _.get(m, 'timetable_details', []).map(d => ({
        ...d,
        subject: m?.subject,
        teacher: m?.teacher,
        is_holiday: m?.is_holiday,
      }))
    )
    .filter(d => {
      const st = dayjs(d?.start_time)
      return st.isAfter(today) && st.isBefore(tomorrow) && !dayjs(d?.end_time).isBefore(dayjs())
    })
    .sortBy(d => dayjs(d?.start_time).valueOf())
    .value()

  if (!upcoming.length && !isLoading) return null

  return (
    <div className="flex flex-col gap-2">
      <HeaderWithIconLine
        renderTitle={() => (
          <span className="flex items-center gap-1.5 font-bold text-sm text-indigo-700 uppercase tracking-wider">
            <PiClockCountdownFill size={15} className="text-indigo-500"/>
            {t('upcoming')} · {t('timetables')}
          </span>
        )}
      />
      {isLoading ? (
        <div className="h-10 background-100 rounded-lg animate-pulse"/>
      ) : (
        <div className="flex flex-col gap-2">
          {upcoming.map((e, i) => {
            const {start_time, end_time, location_address, subject, teacher, is_holiday, notes} = e ?? {}
            const isToday = dayjs(start_time).isSame(dayjs(), 'day')
            return (
              <div key={i} className="flex items-start gap-3 px-3 py-2 rounded-xl border border-indigo-100 bg-indigo-50">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-indigo-100 flex flex-col items-center justify-center">
                  <span className="font-extrabold text-base text-indigo-700 leading-none">
                    {formatDate(start_time, 'HH:mm')}
                  </span>
                  <span className="text-2xs text-indigo-400 leading-none mt-0.5">
                    {isToday ? t('today') : t('tomorrow')}
                  </span>
                </div>
                <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                  {is_holiday === 1 ? (
                    <span className="font-semibold text-sm text-primary-500 italic">{t('holiday')}</span>
                  ) : (
                    <>
                      {subject && (
                        <span className="font-semibold text-sm text-indigo-800 leading-snug">
                          {getTitle(subject)}
                        </span>
                      )}
                      {teacher && (
                        <div className="flex items-center gap-1 text-xs text-color-400">
                          <GoDotFill size={8}/>
                          <span>{`${teacher?.prefix_title || ''} ${teacher?.fullname || getTitle(teacher)}`}</span>
                        </div>
                      )}
                      {location_address && (
                        <div className="flex items-center gap-1 text-xs text-color-400 italic">
                          <GoDotFill size={8}/>
                          <span>{location_address}</span>
                        </div>
                      )}
                      <span className="text-xs text-indigo-400">
                        {formatDate(start_time, 'HH:mm')} – {formatDate(end_time, 'HH:mm')}
                      </span>
                    </>
                  )}
                  {notes && (
                    <span className="text-xs italic text-color-400 max-lines-1">{notes}</span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const ClassRecentAnnouncements = () => {
  const t = useTranslate()
  const {item} = useContext(CourseClassContext)
  const id = getId(item)

  const {response, isLoading} = useAsync({
    apiInfo: announcement_gets_Api,
    values: {owner_type: 'course_class', owner_id: id},
  })

  const items = getResponseItems(response)
  const visible = (items || []).slice(0, 3)

  if (!visible.length && !isLoading) return null

  return (
    <div className="flex flex-col gap-2">
      <HeaderWithIconLine
        renderTitle={() => (
          <span className="flex items-center gap-1.5 font-bold text-sm text-blue-700 uppercase tracking-wider">
            <BsMegaphoneFill size={13} className="text-blue-500"/>
            {t('announcements')}
          </span>
        )}
      />
      {isLoading ? (
        <div className="h-10 background-100 rounded-lg animate-pulse"/>
      ) : (
        <div className="flex flex-col gap-2">
          {visible.map((e, i) => {
            const typeItem = _.find(AnnouncementTypes, {value: e?.type_code || e?.type_label})
            return (
              <div key={i} className="flex flex-col gap-1 px-3 py-2 rounded-xl border border-blue-100 bg-blue-50">
                <div className="flex items-center gap-1.5">
                  {typeItem?.icon}
                  <span className="text-xs font-semibold text-blue-600">{e?.type_label}</span>
                </div>
                <span className="font-semibold text-sm text-color-100 leading-snug">{e?.title}</span>
                {e?.content && (
                  <span className="text-xs text-color-400 max-lines-2 whitespace-pre-line">{e?.content}</span>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const CourseClassInformationTab = () => {
  const {item, renderWarnings = NullAsDefault} = useContext(CourseClassContext)
  const {course} = item ?? {}

  const warnings = Array.from(item?.warnings ?? [])

  if (warnings.length) {
    return (
      <div className="w-full flex flex-col gap-3">
        {renderWarnings(warnings)}
      </div>
    )
  }

  return renderIf(
    item,
    <div className="flex flex-col gap-4 py-2">
      <CourseContextCard course={course} />
      <ClassMetaCard item={item} />
      <ClassUpcomingTimetable />
      <ClassRecentAnnouncements />
    </div>
  )
}

export default CourseClassInformationTab
